// client/src/components/doctors/Appointments/ViewPaymentConfirmation.jsx - ENHANCED
import React, { useState } from 'react';
import '../../../styles/components/doctor/ViewPaymentConfirmation.css';
import { format } from 'date-fns';

import paymentModalIndicator from '../../../assets/icons/indicators/Indicator_Payment_Modal.png';

const ViewPaymentConfirmation = ({ 
  payment, 
  onClose, 
  onConfirmPayment,
  onRejectPayment, 
  isLoading = false
}) => {
  const [showFullImage, setShowFullImage] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  if (!payment) return null;

  // ✅ Enhanced payment status detection
  const getPaymentStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return { color: '#FF9800', text: 'Pending Payment' };
      case 'proof_uploaded':
        return { color: '#2196F3', text: 'Proof Uploaded - Awaiting Verification' };
      case 'paid':
        return { color: '#4CAF50', text: 'Payment Confirmed' };
      case 'rejected':
        return { color: '#F44336', text: 'Payment Proof Rejected' };
      default:
        return { color: '#757575', text: status || 'Unknown' };
    }
  };

  const statusInfo = getPaymentStatusInfo(payment.status);

  // ✅ Handle rejection
  const handleRejectPayment = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await onRejectPayment?.(rejectionReason);
      setShowRejectModal(false);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('Failed to reject payment. Please try again.');
    }
  };

  // ✅ Image Modal Component
  const ImageModal = () => {
    if (!showFullImage || !payment.proofImage) return null;

    return (
      <div className="payment-image-modal-overlay" onClick={() => setShowFullImage(false)}>
        <div className="payment-image-modal" onClick={(e) => e.stopPropagation()}>
          <div className="payment-image-modal-header">
            <h3>Payment Proof</h3>
            <button 
              className="payment-image-close-btn" 
              onClick={() => setShowFullImage(false)}
            >
              ×
            </button>
          </div>
          <div className="payment-image-modal-body">
            <img 
              src={payment.proofImage} 
              alt="Payment Proof" 
              className="payment-image-full"
            />
          </div>
          <div className="payment-image-modal-footer">
            <button 
              className="payment-image-download-btn"
              onClick={() => {
                const link = document.createElement('a');
                link.href = payment.proofImage;
                link.download = `payment-proof-${payment.transactionId || 'unknown'}.jpg`;
                link.click();
              }}
            >
              Download Image
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ✅ Rejection Modal Component
  const RejectionModal = () => {
    if (!showRejectModal) return null;

    return (
      <div className="payment-rejection-modal-overlay">
        <div className="payment-rejection-modal">
          <div className="payment-rejection-header">
            <h3>Reject Payment Proof</h3>
            <button 
              className="payment-rejection-close-btn" 
              onClick={() => setShowRejectModal(false)}
            >
              ×
            </button>
          </div>
          <div className="payment-rejection-body">
            <p>Please provide a reason for rejecting this payment proof:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason (e.g., unclear image, incorrect amount, invalid receipt, etc.)"
              className="payment-rejection-textarea"
              rows={4}
            />
          </div>
          <div className="payment-rejection-footer">
            <button 
              className="payment-rejection-cancel-btn" 
              onClick={() => setShowRejectModal(false)}
            >
              Cancel
            </button>
            <button 
              className="payment-rejection-confirm-btn" 
              onClick={handleRejectPayment}
              disabled={!rejectionReason.trim()}
            >
              Reject Payment
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="payment-confirmation-modal-overlay">
        <div className="payment-confirmation-modal">
          <div className="payment-confirmation-header">
            <button 
              className="payment-confirmation-back-btn" 
              onClick={onClose}
            >
              <span>&lt;</span>
            </button>
            <h3 className="payment-confirmation-title">View Payment Confirmation</h3>
            <div className="doctor-appointments-payment-icon-container">
                        <img 
                          src={paymentModalIndicator} 
                          alt="Payment Options" 
                          className="doctor-appointments-payment-header-icon"
                        />
                      </div>
          </div>
          
          <div className="payment-confirmation-body">
            <div className="payment-confirmation-section">
            <div className="payment-confirmation-section-merge-container">
              <h4 className="payment-confirmation-section-title">Patient</h4>
              <div className="payment-confirmation-field">
                {payment.patientName || 'Unknown Patient'}
              </div>
            

            <div className="payment-confirmation-section">
              <h4 className="payment-confirmation-section-title">Appointment Schedule</h4>
              <div className="payment-confirmation-field">
                {payment.appointmentDate ? 
                  format(new Date(payment.appointmentDate), 'MMMM d, yyyy - h:mm a') : 
                  'Not scheduled'}
              </div>
            </div>
            </div>
          </div>
        

            {/* ✅ ENHANCED: Payment Status with Visual Indicator */}
            <div className="payment-confirmation-section">
              <h4 className="payment-confirmation-section-title">Payment Status</h4>
              <div className="payment-status-container">
                <span 
                  className="payment-status-badge"
                  style={{ 
                    backgroundColor: statusInfo.color + '20', 
                    color: statusInfo.color,
                    border: `1px solid ${statusInfo.color}`
                  }}
                >
                  {statusInfo.text}
                </span>
              </div>
            </div>

            <div className="payment-confirmation-section">
              <h4 className="payment-confirmation-section-title">Proof of Payment</h4>
              
              <div className="payment-confirmation-details">
                <div className="payment-confirmation-detail-row">
                  <span className="payment-confirmation-label">Date Uploaded:</span>
                  <span className="payment-confirmation-value">
                    {payment.paymentDate ? 
                      format(new Date(payment.paymentDate), 'MMMM d, yyyy - h:mm a') : 
                      'Not available'}
                  </span>
                </div>
                
                {payment.paymentMethod && (
                  <div className="payment-confirmation-detail-row">
                    <span className="payment-confirmation-label">Payment Method:</span>
                    <span className="payment-confirmation-value">
                      {payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1)}
                    </span>
                  </div>
                )}
                
                {payment.transactionId && (
                  <div className="payment-confirmation-detail-row">
                    <span className="payment-confirmation-label">Transaction ID:</span>
                    <span className="payment-confirmation-value">{payment.transactionId}</span>
                  </div>
                )}
                
                {payment.amount && (
                  <div className="payment-confirmation-detail-row">
                    <span className="payment-confirmation-label">Amount:</span>
                    <span className="payment-confirmation-value">₱{payment.amount.toLocaleString()}</span>
                  </div>
                )}
              </div>
              
              {/* ✅ ENHANCED: Payment Proof Image Display */}
              {payment.proofImage ? (
                <div className="payment-confirmation-image-container">
                  <div className="payment-confirmation-image-wrapper">
                    <img 
                      src={payment.proofImage} 
                      alt="Proof of Payment" 
                      className="payment-confirmation-image"
                      onClick={() => setShowFullImage(true)}
                    />
                  </div>
                  
                  {/* ✅ Image Actions */}
                  <div className="payment-confirmation-image-actions">
                    <button 
                      className="payment-confirmation-download-btn"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = payment.proofImage;
                        link.download = `payment-proof-${payment.transactionId || 'unknown'}.jpg`;
                        link.click();
                      }}
                    >
                      View Full Image
                    </button>
                  </div>
                </div>
              ) : (
                <div className="payment-confirmation-no-image">
                  <div className="payment-confirmation-no-image-icon">📷</div>
                  <p>No payment proof uploaded</p>
                </div>
              )}
            </div>

            {/* ✅ NEW: Rejection History (if payment was rejected) */}
            {payment.status === 'rejected' && payment.rejectionReason && (
              <div className="payment-confirmation-section">
                <h4 className="payment-confirmation-section-title">Rejection Details</h4>
                <div className="payment-rejection-info">
                  <div className="payment-confirmation-detail-row">
                    <span className="payment-confirmation-label">Rejected At:</span>
                    <span className="payment-confirmation-value">
                      {payment.rejectedAt ? 
                        format(new Date(payment.rejectedAt), 'MMMM d, yyyy - h:mm a') : 
                        'Unknown'}
                    </span>
                  </div>
                  <div className="payment-confirmation-detail-row">
                    <span className="payment-confirmation-label">Reason:</span>
                    <span className="payment-confirmation-value">{payment.rejectionReason}</span>
                  </div>
                  {payment.rejectionNotes && (
                    <div className="payment-confirmation-detail-row">
                      <span className="payment-confirmation-label">Notes:</span>
                      <span className="payment-confirmation-value">{payment.rejectionNotes}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="payment-confirmation-footer">
            {/* ✅ ENHANCED: Action buttons based on payment status */}
            {payment.status === 'proof_uploaded' && (
              <div className="payment-confirmation-actions">
                <button 
                  className="payment-confirmation-reject-btn"
                  onClick={() => setShowRejectModal(true)}
                  disabled={isLoading}
                >
                  Reject Proof
                </button>
                <button 
                  className={`payment-confirmation-confirm-btn ${isLoading ? 'loading' : ''}`} 
                  onClick={onConfirmPayment}
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : 'Confirm Payment'}
                </button>
              </div>
            )}
            
            {payment.status === 'paid' && (
              <div className="payment-confirmation-confirmed-badge">
                ✅ Payment Confirmed
              </div>
            )}

            {payment.status === 'rejected' && (
              <div className="payment-confirmation-rejected-badge">
                ❌ Payment Rejected
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ Image Modal */}
      <ImageModal />
      
      {/* ✅ Rejection Modal */}
      <RejectionModal />
    </>
  );
};

export default ViewPaymentConfirmation;